/**
 * Two devices on one draft (Phase B3): a phone scans cards onto the trade open on the till, and
 * staff edit it on the till while the phone shows it. Each polls GET /trades/:id/version
 * (`{ updatedAt, itemCount, status }`) every few seconds while visible and re-reads the whole trade
 * only when it moved on. Every item write bumps the trade's updatedAt on the server.
 *
 * A device's own writes come back with the trade they produced (and its updatedAt), so they don't
 * look like news; what is left is the other device's edits.
 */

/** GET /trades/:id/version. */
export type TradeVersion = { updatedAt: string; itemCount: number; status: string }

/** How often a visible page asks. */
export const TRADE_POLL_MS = 4000

const time = (value: string | null | undefined): number => {
  const t = value ? Date.parse(value) : NaN
  return Number.isFinite(t) ? t : NaN
}

/**
 * The trade on the server is not the one on screen: it was written after the copy this page holds,
 * or it is no longer a draft. No copy yet (or one without a date) counts as moved on.
 */
export function versionMoved(known: { updatedAt?: string | null; status?: string } | null | undefined, polled: TradeVersion): boolean {
  if (!known) return true
  if (known.status && polled.status !== known.status) return true
  const a = time(known.updatedAt)
  const b = time(polled.updatedAt)
  return !Number.isFinite(a) || !Number.isFinite(b) || a !== b
}

/** What the poll does with a moved trade: re-read it now, or once this page's own edit is done. */
export function refreshAction(moved: boolean, busy: boolean): 'none' | 'refresh' | 'defer' {
  if (!moved) return 'none'
  return busy ? 'defer' : 'refresh'
}

type LineLike = { id?: unknown; side?: unknown }

/** Customer lines in `next` that `prev` didn't have: the cards another device just put on. */
export function newCustomerLineIds(prev: { items?: LineLike[] } | null | undefined, next: { items?: LineLike[] } | null | undefined): string[] {
  const before = new Set((prev?.items ?? []).map((i) => String(i.id)))
  return (next?.items ?? [])
    .filter((i) => i.side === 'customer' && typeof i.id === 'string' && !before.has(i.id))
    .map((i) => String(i.id))
}
